import { useRef, useState } from 'react';
import { formatVideoTime } from '../core/time';
import { useThumbnails } from '../media/thumbs';
import { browserSupport, probeVideo } from '../media/probe';
import { forgetProject, loadProject } from '../state/persist';
import { fileKeyOf, useApp } from '../state/store';
import { Button, Chip, Icon, Panel, ScoreBug } from '../ui';

function formatSize(bytes: number) {
  return bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.round(bytes / 1e6)} MB`;
}

export async function pickFile(file: File) {
  const app = useApp.getState();
  app.setFile(file);
  const saved = await loadProject(fileKeyOf(file));
  if (useApp.getState().file !== file) return;
  if (saved) useApp.getState().setRestorable(saved);
  try {
    const info = await probeVideo(file);
    if (useApp.getState().file === file) useApp.setState({ info });
  } catch (e) {
    if (useApp.getState().file === file) useApp.setState({ probeError: e instanceof Error ? e.message : 'This file could not be read.' });
  }
}

export function StartScreen() {
  const { file, info, probeError, restorable, project, update, setStep, setRestorable, restore, fileKey } = useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const support = browserSupport();
  const thumbs = useThumbnails(info ? file : null, info ? [Math.min(600, info.duration / 3)] : []);
  const thumb = thumbs.values().next().value;
  const isList = project.method === 'list';
  const ready = !!file && !!info && !probeError;

  const onFiles = (files: FileList | null) => {
    const f = files?.[0];
    if (f) void pickFile(f);
  };

  const next = () => {
    if (restorable && fileKey) {
      void forgetProject(fileKey);
      setRestorable(null);
    }
    setStep(isList ? 'timestamps' : 'mark');
  };

  return (
    <div className="start">
      <div className="start-hero" style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingTop: 16 }}>
        <div className="app-row" style={{ gap: 8 }}>
          <Chip tone="signal">Free</Chip>
          <Chip>No upload</Chip>
          <Chip>No account</Chip>
        </div>
        <h1>Turn the full match into a highlight reel.</h1>
        <p>Pick the match video, then paste your timestamps or mark each highlight live while you watch. Clips are cut in your browser, so the video never leaves your computer.</p>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: 'var(--ink-muted)' }}>
          <li className="app-row">
            <Icon name="lock" size={18} />
            The file is read in place. Nothing is uploaded, not even a thumbnail.
          </li>
          <li className="app-row">
            <Icon name="list" size={18} />
            Works with Veo lists, any list of times, or marks you make yourself.
          </li>
          <li className="app-row">
            <Icon name="monitor" size={18} />
            Built for Chrome and Edge on a laptop or desktop.
          </li>
        </ul>
        <details className="text-small muted">
          <summary style={{ cursor: 'pointer', color: 'var(--ink)', fontWeight: 500, minHeight: 32, display: 'flex', alignItems: 'center' }}>Where do I find my Veo timestamps?</summary>
          <p style={{ fontSize: 14 }}>
            On Veo, open the match and the player's highlight list with both periods expanded. Select the whole list, copy it and paste it here as it is. Lines like <span className="time">02:5914s</span> mean “starts at 02:59, lasts 14 s”. Names and other text are ignored.
          </p>
        </details>
      </div>

      <Panel aria-labelledby="start-title" style={{ padding: 28, gap: 28 }}>
        <h2 id="start-title" className="hl-sr">
          Start a reel
        </h2>
        {!support.ok && (
          <div className="app-banner app-banner-danger" role="alert">
            <Icon name="alert" />
            {support.reason}
          </div>
        )}
        <div data-tour="video" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p className="app-label">1 · Match video</p>
          <input ref={inputRef} type="file" accept="video/*,.mp4,.mov,.m4v" className="hl-sr" onChange={(e) => onFiles(e.target.files)} aria-label="Choose match video" />
          {!file ? (
            <div
              className={`dropzone${over ? ' is-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                onFiles(e.dataTransfer.files);
              }}
            >
              <span style={{ width: 52, height: 52, borderRadius: 'var(--radius-sm)', background: 'var(--signal-soft)', color: 'var(--signal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="upload" size={26} />
              </span>
              <span style={{ fontSize: 18, fontWeight: 600 }}>Drop your match video here</span>
              <span className="text-small muted">MP4 or MOV from your camera or Veo. Files of several GB are fine.</span>
              <Button icon="upload" onClick={() => inputRef.current?.click()}>
                Choose video file
              </Button>
            </div>
          ) : (
            <div className="file-card">
              <span className="file-thumb">
                {thumb && <img src={thumb} alt="" />}
                {info && (
                  <span style={{ position: 'absolute', left: 6, bottom: 6 }}>
                    <ScoreBug time={formatVideoTime(info.duration)} />
                  </span>
                )}
              </span>
              <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="app-row" style={{ gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                  {info && !probeError && (
                    <Chip tone="pitch" icon="check">
                      Loaded
                    </Chip>
                  )}
                  {!info && !probeError && <Chip icon="loader">Reading</Chip>}
                </span>
                <span className="time text-small muted">
                  {formatSize(file.size)}
                  {info && ` · ${info.width} × ${info.height}${info.fps ? ` · ${info.fps} fps` : ''}`}
                  {info && !info.hasAudio && ' · no sound'}
                </span>
              </div>
              <Button size="sm" onClick={() => inputRef.current?.click()}>
                Replace
              </Button>
            </div>
          )}
          {probeError && (
            <div className="app-banner app-banner-danger" role="alert">
              <Icon name="alert" />
              {probeError} Try an MP4 file with H.264 video, the format Veo and most cameras export.
            </div>
          )}
          {info && !info.canDecode && (
            <div className="app-banner app-banner-warning" role="alert">
              <Icon name="alert" />
              This browser can't decode this video ({info.codec}). Preview and export may fail; Chrome or Edge usually can.
            </div>
          )}
          {restorable && (
            <div className="app-banner app-banner-cue">
              <Icon name="clock" />
              <span style={{ flexGrow: 1 }}>You worked on this video before. Pick up where you left off?</span>
              <Button size="sm" variant="primary" onClick={() => {
                restore();
                const m = useApp.getState().project.method;
                const hasKickoffs = Object.keys(useApp.getState().project.kickoffs).length > 0;
                setStep(m === 'live' ? 'clips' : hasKickoffs ? 'clips' : 'timestamps');
              }}>
                Restore
              </Button>
              <Button size="sm" onClick={() => {
                if (fileKey) void forgetProject(fileKey);
                setRestorable(null);
              }}>
                Start fresh
              </Button>
            </div>
          )}
        </div>

        <fieldset data-tour="method" style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <legend className="app-label" style={{ padding: 0, marginBottom: 12 }}>
            2 · How do you want to find the highlights?
          </legend>
          <div className="method-grid">
            <label className={`method${isList ? ' is-on' : ''}`}>
              <input type="radio" name="method" className="hl-sr" checked={isList} onChange={() => update({ method: 'list' })} />
              <span className="app-row-between">
                <span style={{ width: 44, height: 44, borderRadius: 'var(--radius-sm)', background: 'var(--surface-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="list" size={22} />
                </span>
                <span className="method-dot" />
              </span>
              <span className="method-title">From a timestamp list</span>
              <span className="text-small muted">Paste a Veo list or your own times. Line up the kickoffs once, then review every clip.</span>
              <span className="app-row" style={{ gap: 6, marginTop: 'auto' }}>
                <Chip>Veo list</Chip>
                <Chip>Any list</Chip>
              </span>
            </label>
            <label className={`method${!isList ? ' is-on' : ''}`}>
              <input type="radio" name="method" className="hl-sr" checked={!isList} onChange={() => update({ method: 'live' })} />
              <span className="app-row-between">
                <span style={{ width: 44, height: 44, borderRadius: 'var(--radius-sm)', background: 'var(--signal-soft)', color: 'var(--signal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="flag" size={22} />
                </span>
                <span className="method-dot" />
              </span>
              <span className="method-title">Mark it live</span>
              <span className="text-small muted">Play the match. Press once where a highlight starts and again where it ends. Each clip goes straight into your reel.</span>
              <span className="app-row" style={{ gap: 6, marginTop: 'auto' }}>
                <Chip tone="cue">No kickoff sync</Chip>
              </span>
            </label>
          </div>
        </fieldset>

        <div className="app-row-between" style={{ paddingTop: 20, borderTop: '1px solid var(--line)' }}>
          <span className="text-small muted">{isList ? 'Next: paste your list, then sync the kickoffs.' : 'Next: play the match and mark each highlight.'}</span>
          <Button variant="primary" size="lg" icon="arrow-right" iconAfter disabled={!ready} onClick={next}>
            {!file ? 'Choose a video first' : isList ? 'Paste timestamps' : 'Start marking'}
          </Button>
        </div>
      </Panel>
    </div>
  );
}
